/**
 * Remotion Carousel Editor
 *
 * Main editor component that combines:
 * - Remotion Player for WYSIWYG preview
 * - Canvas editing layer for interactions
 * - Sidebar controls for properties
 *
 * This demonstrates how the Player and editing layer work together.
 */

import React, { useState, useCallback } from 'react';
import { Player } from '@remotion/player';
import { CarouselSlide } from '../compositions/CarouselSlide';
import { CanvasEditingLayer } from './CanvasEditingLayer';
import { OverlayData, CarouselSlideProps, TextOverlayData } from '../types';
import { fitTextOverlay } from '../utils/textFitting';

// Demo dimensions (Instagram carousel)
const SLIDE_WIDTH = 1080;
const SLIDE_HEIGHT = 1350;
const PREVIEW_SCALE = 0.4; // Scale down for preview

interface RemotionCarouselEditorProps {
  initialOverlays?: OverlayData[];
  backgroundColor?: string;
  backgroundImage?: string;
  onOverlaysChange?: (overlays: OverlayData[]) => void;
}

export const RemotionCarouselEditor: React.FC<RemotionCarouselEditorProps> = ({
  initialOverlays = [],
  backgroundColor = '#1a1a2e',
  backgroundImage,
  onOverlaysChange,
}) => {
  const [overlays, setOverlays] = useState<OverlayData[]>(initialOverlays);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const selectedOverlay = overlays.find((o) => o.id === selectedId);

  // Update an overlay with auto-fit for text
  const handleUpdate = useCallback(
    (id: string, updates: Partial<OverlayData>) => {
      setOverlays((prev) => {
        const updated = prev.map((o) => {
          if (o.id !== id) return o;

          // Merge updates
          const merged = { ...o, ...updates } as OverlayData;

          // Auto-fit text when height is set/changed
          if (
            merged.type === 'text' &&
            updates.height !== undefined &&
            merged.height !== undefined
          ) {
            const textOverlay = merged as TextOverlayData;
            const fittedFontSize = fitTextOverlay(
              textOverlay,
              SLIDE_WIDTH,
              SLIDE_HEIGHT
            );

            // Only shrink, don't grow beyond original
            if (fittedFontSize < textOverlay.style.fontSize) {
              return {
                ...textOverlay,
                style: {
                  ...textOverlay.style,
                  fontSize: fittedFontSize,
                },
              } as OverlayData;
            }
          }

          return merged;
        });
        onOverlaysChange?.(updated);
        return updated;
      });
    },
    [onOverlaysChange]
  );

  // Delete an overlay
  const handleDelete = useCallback(
    (id: string) => {
      setOverlays((prev) => {
        const updated = prev.filter((o) => o.id !== id);
        onOverlaysChange?.(updated);
        return updated;
      });
      setSelectedId(null);
    },
    [onOverlaysChange]
  );

  // Add a new text overlay
  const handleAddText = useCallback(() => {
    const newOverlay: OverlayData = {
      id: `text-${Date.now()}`,
      type: 'text',
      text: 'New Text',
      position: { x: 10, y: 10 },
      width: 80,
      zIndex: overlays.length,
      style: {
        fontFamily: 'Inter',
        fontSize: 48,
        color: '#ffffff',
        backgroundColor: '#000000',
        opacity: 80,
        textAlign: 'center',
        fontWeight: 'bold',
        fontStyle: 'normal',
        lineHeight: 1.4,
        borderRadius: 8,
        padding: { top: 16, right: 24, bottom: 16, left: 24 },
      },
    };

    setOverlays((prev) => {
      const updated = [...prev, newOverlay];
      onOverlaysChange?.(updated);
      return updated;
    });
    setSelectedId(newOverlay.id);
  }, [overlays.length, onOverlaysChange]);

  // Preview dimensions
  const previewWidth = SLIDE_WIDTH * PREVIEW_SCALE;
  const previewHeight = SLIDE_HEIGHT * PREVIEW_SCALE;

  // Composition props
  const compositionProps: CarouselSlideProps = {
    width: SLIDE_WIDTH,
    height: SLIDE_HEIGHT,
    backgroundColor,
    backgroundImage,
    overlays,
  };

  return (
    <div style={{ display: 'flex', gap: 24, padding: 24 }}>
      {/* Canvas Area */}
      <div>
        <h3 style={{ marginBottom: 16 }}>Preview (WYSIWYG)</h3>
        <div
          style={{
            position: 'relative',
            width: previewWidth,
            height: previewHeight,
            borderRadius: 8,
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          }}
        >
          {/* Remotion Player - renders the actual composition */}
          <Player
            component={CarouselSlide}
            inputProps={compositionProps}
            durationInFrames={1}
            fps={30}
            compositionWidth={SLIDE_WIDTH}
            compositionHeight={SLIDE_HEIGHT}
            style={{
              width: previewWidth,
              height: previewHeight,
              pointerEvents: 'none', // Disable Player interactions
            }}
            controls={false}
          />

          {/* Editing layer on top */}
          <CanvasEditingLayer
            overlays={overlays}
            containerWidth={previewWidth}
            containerHeight={previewHeight}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onUpdate={(id, updates) => {
              // Convert pixel-based updates if needed
              handleUpdate(id, updates);
            }}
            onDelete={handleDelete}
            snapToGrid={true}
            gridSize={5}
            scale={PREVIEW_SCALE}
          />
        </div>

        {/* Toolbar */}
        <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
          <button
            onClick={handleAddText}
            style={{
              padding: '8px 16px',
              backgroundColor: '#2196F3',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
            }}
          >
            + Add Text
          </button>
        </div>
      </div>

      {/* Properties Panel */}
      <div style={{ width: 300 }}>
        <h3 style={{ marginBottom: 16 }}>Properties</h3>

        {selectedOverlay ? (
          <PropertiesPanel
            overlay={selectedOverlay}
            onUpdate={(updates) => handleUpdate(selectedId!, updates)}
            onDelete={() => handleDelete(selectedId!)}
          />
        ) : (
          <p style={{ color: '#888' }}>Select an overlay to edit its properties</p>
        )}
      </div>
    </div>
  );
};

// Simple properties panel
interface PropertiesPanelProps {
  overlay: OverlayData;
  onUpdate: (updates: Partial<OverlayData>) => void;
  onDelete: () => void;
}

const PropertiesPanel: React.FC<PropertiesPanelProps> = ({
  overlay,
  onUpdate,
  onDelete,
}) => {
  if (overlay.type === 'text') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <label>
          Text:
          <textarea
            value={overlay.text}
            onChange={(e) => onUpdate({ text: e.target.value })}
            style={{
              width: '100%',
              padding: 8,
              marginTop: 4,
              borderRadius: 4,
              border: '1px solid #ccc',
              minHeight: 80,
            }}
          />
        </label>

        <label>
          Font Size: {overlay.style.fontSize}px
          <input
            type="range"
            min={12}
            max={120}
            value={overlay.style.fontSize}
            onChange={(e) =>
              onUpdate({
                style: { ...overlay.style, fontSize: parseInt(e.target.value) },
              })
            }
            style={{ width: '100%' }}
          />
        </label>

        <label>
          Text Color:
          <input
            type="color"
            value={overlay.style.color}
            onChange={(e) =>
              onUpdate({
                style: { ...overlay.style, color: e.target.value },
              })
            }
            style={{ marginLeft: 8 }}
          />
        </label>

        <label>
          Background Color:
          <input
            type="color"
            value={overlay.style.backgroundColor}
            onChange={(e) =>
              onUpdate({
                style: { ...overlay.style, backgroundColor: e.target.value },
              })
            }
            style={{ marginLeft: 8 }}
          />
        </label>

        <label>
          Background Opacity: {overlay.style.opacity}%
          <input
            type="range"
            min={0}
            max={100}
            value={overlay.style.opacity}
            onChange={(e) =>
              onUpdate({
                style: { ...overlay.style, opacity: parseInt(e.target.value) },
              })
            }
            style={{ width: '100%' }}
          />
        </label>

        <label>
          Text Align:
          <select
            value={overlay.style.textAlign}
            onChange={(e) =>
              onUpdate({
                style: {
                  ...overlay.style,
                  textAlign: e.target.value as 'left' | 'center' | 'right',
                },
              })
            }
            style={{ marginLeft: 8, padding: 4 }}
          >
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </select>
        </label>

        <label>
          Font Weight:
          <select
            value={overlay.style.fontWeight}
            onChange={(e) =>
              onUpdate({
                style: {
                  ...overlay.style,
                  fontWeight: e.target.value as 'normal' | 'bold',
                },
              })
            }
            style={{ marginLeft: 8, padding: 4 }}
          >
            <option value="normal">Normal</option>
            <option value="bold">Bold</option>
          </select>
        </label>

        <div style={{ marginTop: 8 }}>
          <strong>Position:</strong>
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <label>
              X: {overlay.position.x.toFixed(1)}%
              <input
                type="range"
                min={0}
                max={100}
                step={0.5}
                value={overlay.position.x}
                onChange={(e) =>
                  onUpdate({
                    position: { ...overlay.position, x: parseFloat(e.target.value) },
                  })
                }
                style={{ width: '100%' }}
              />
            </label>
            <label>
              Y: {overlay.position.y.toFixed(1)}%
              <input
                type="range"
                min={0}
                max={100}
                step={0.5}
                value={overlay.position.y}
                onChange={(e) =>
                  onUpdate({
                    position: { ...overlay.position, y: parseFloat(e.target.value) },
                  })
                }
                style={{ width: '100%' }}
              />
            </label>
          </div>
        </div>

        <div style={{ marginTop: 8 }}>
          <strong>Size:</strong>
          <label>
            Width: {overlay.width.toFixed(1)}%
            <input
              type="range"
              min={5}
              max={100}
              step={0.5}
              value={overlay.width}
              onChange={(e) => onUpdate({ width: parseFloat(e.target.value) })}
              style={{ width: '100%' }}
            />
          </label>
          <div style={{ marginTop: 4, color: '#888', fontSize: 12 }}>
            Height: Auto (based on content)
          </div>
        </div>

        <button
          onClick={onDelete}
          style={{
            marginTop: 16,
            padding: '8px 16px',
            backgroundColor: '#f44336',
            color: 'white',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
          }}
        >
          Delete Overlay
        </button>
      </div>
    );
  }

  // For image overlays (simplified)
  return (
    <div>
      <p>Image Overlay</p>
      <button onClick={onDelete}>Delete</button>
    </div>
  );
};

export default RemotionCarouselEditor;
